"use client"; // This makes it a client component

import type { IconProp } from "@fortawesome/fontawesome-svg-core";
import {
	faBullseye,
	faChartBar,
	faChevronDown,
	faChevronUp,
	faCog,
	faCookie,
	faShieldAlt,
	faTimes,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { motion } from "framer-motion";
import type React from "react";
import { useEffect, useState } from "react";

// Extend the Window interface to include Google Analytics properties
declare global {
	interface Window {
		"ga-disable-analytics_TRACKING"?: boolean;
		gtag?: (...args: unknown[]) => void;
	}
}

// Define types
interface ConsentState {
	essential: boolean;
	analytics: boolean;
	marketing: boolean;
}

interface CookieCategory {
	title: string;
	description: string;
	cookies: string[];
	icon: IconProp;
	required: boolean;
}

interface CookieCategories {
	[key: string]: CookieCategory;
}

// Add this validation function
const isValidConsentState = (obj: unknown): obj is ConsentState => {
	if (typeof obj !== "object" || obj === null) {
		return false;
	}

	const consent = obj as Record<string, unknown>;
	return (
		typeof consent.essential === "boolean" &&
		typeof consent.analytics === "boolean" &&
		typeof consent.marketing === "boolean"
	);
};

// Main CookieBanner component
const CookieBanner: React.FC = () => {
	const [showBanner, setShowBanner] = useState(false);
	const [showPanel, setShowPanel] = useState(false);
	const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({});
	const [consent, setConsent] = useState<ConsentState>({
		essential: true,
		analytics: false,
		marketing: false,
	});

	// Cookie categories
	const cookieCategories: CookieCategories = {
		essential: {
			title: "Cookies Esenciales",
			description: "Necesarias para el funcionamiento básico del sitio web y el procesamiento de pagos.",
			cookies: [
				"__stripe_mid - Identificador de Stripe para procesamiento de pagos",
				"__stripe_sid - Sesión de Stripe para transacciones seguras",
			],
			icon: faShieldAlt,
			required: true,
		},
		analytics: {
			title: "Cookies de Análisis",
			description: "Nos ayudan a entender cómo los visitantes interactúan con nuestro sitio web.",
			cookies: [
				"_ga - Google Analytics: identificador único de usuario",
				"_ga_[aleatorio] - Google Analytics: datos de sesión específicos",
				"_ga_[aleatorio] - Google Analytics: métricas de comportamiento",
				"_ga_[aleatorio] - Google Analytics: datos de conversión",
				"tk_ai - Jetpack: identificador de usuario para análisis",
				"tk_or - Jetpack: seguimiento del origen del tráfico",
				"tk_lr - Jetpack: última URL de referencia",
			],
			icon: faChartBar,
			required: false,
		},
		/* ,
    marketing: {
      title: 'Cookies de Marketing',
      description: 'Se utilizan para rastrear visitantes.',
      cookies: [
        'tk_ai - Jetpack: identificador de usuario para análisis',
        'tk_or - Jetpack: seguimiento del origen del tráfico',
        'tk_lr - Jetpack: última URL de referencia'
      ],
      icon: faBullseye,
      required: false
    } */
	};

	// Check if user has already made a choice
	const hasUserMadeChoice = () => {
		return localStorage.getItem("cookie-consent") !== null;
	};

	// Check consent status on component mount
	useEffect(() => {
		const savedConsent = localStorage.getItem("cookie-consent");
		const consentTimestamp = localStorage.getItem("cookie-consent-timestamp");

		if (savedConsent && consentTimestamp) {
			// Check if consent is less than 1 year old
			const oneYear = 365 * 24 * 60 * 60 * 1000;
			if (Date.now() - parseInt(consentTimestamp) < oneYear) {
				try {
					// Parse and validate the consent object
					const parsedConsent = JSON.parse(savedConsent);

					// Validate that the parsed object matches ConsentState structure
					if (isValidConsentState(parsedConsent)) {
						setConsent(parsedConsent);
						applyConsent(parsedConsent);
						return;
					} else {
						console.warn("Invalid consent state found in localStorage");
						// Fall through to show banner
					}
				} catch (error) {
					console.error("Failed to parse cookie consent from localStorage:", error);
					// Fall through to show banner
				}
			}
		}

		// Show banner if no valid consent and user hasn't made a choice
		if (!hasUserMadeChoice()) {
			setShowBanner(true);
		}
	}, []);

	// Toggle expanded section
	const toggleSection = (section: string) => {
		setExpandedSections((prev) => ({
			...prev,
			[section]: !prev[section],
		}));
	};

	const applyConsent = (consentChoices: ConsentState) => {
		// Set consent tracking cookies
		document.cookie = `cookie-consent=true; path=/; max-age=${365 * 24 * 60 * 60}`;
		document.cookie = `cookie-consents=${JSON.stringify(consentChoices)}; path=/; max-age=${365 * 24 * 60 * 60}`;

		// Handle Analytics cookies
		if (!consentChoices.analytics) {
			// Disable Google Analytics
			window["ga-disable-analytics_TRACKING"] = true;
			deleteCookies(["_ga", "_ga_PP9VQSJ0DK", "_ga_S55WZQNRYZ", "_ga_QY3DVNQGQN"]);
		} else {
			// Enable Google Analytics
			window["ga-disable-analytics_TRACKING"] = false;
		}

		// Handle Marketing cookies
		if (!consentChoices.marketing) {
			deleteCookies(["tk_ai", "tk_or", "tk_lr"]);
		}

		// Save consent preferences
		localStorage.setItem("cookie-consent", JSON.stringify(consentChoices));
		localStorage.setItem("cookie-consent-timestamp", Date.now().toString());
	};

	const deleteCookies = (cookieNames: string[]) => {
		cookieNames.forEach((name) => {
			document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
		});
	};

	const handleAcceptAll = () => {
		const allConsent: ConsentState = {
			essential: true,
			analytics: true,
			marketing: true,
		};
		setConsent(allConsent);
		applyConsent(allConsent);
		setShowBanner(false);
		setShowPanel(false);
	};

	const handleRejectAll = () => {
		const minimalConsent: ConsentState = {
			essential: true,
			analytics: false,
			marketing: false,
		};
		setConsent(minimalConsent);
		applyConsent(minimalConsent);
		setShowBanner(false);
		setShowPanel(false);
	};

	const handleSavePreferences = () => {
		applyConsent(consent);
		setShowBanner(false);
		setShowPanel(false);
	};

	const handleCategoryChange = (category: keyof ConsentState, value: boolean) => {
		if (category === "essential") return; // Can't disable essential cookies
		setConsent((prev) => ({
			...prev,
			[category]: value,
		}));
	};

	const openSettingsPanel = () => {
		setShowPanel(true);
		setShowBanner(false);
	};

	// Small bottom banner for first-time visitors
	const renderBottomBanner = () => (
		<motion.div
			initial={{ opacity: 0, y: 100 }}
			animate={{ opacity: 1, y: 0 }}
			transition={{ duration: 0.6 }}
			className="fixed bottom-0 left-0 right-0 z-50 bg-black/95 backdrop-blur-sm border-t-2 border-red-800 shadow-lg"
		>
			<div className="container mx-auto max-w-6xl p-4">
				<div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
					<div className="flex items-start gap-3 flex-1">
						<FontAwesomeIcon icon={faCookie} className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
						<div className="text-sm text-gray-200">
							<p className="font-amarante text-lg text-white mb-1">🍪 Este sitio utiliza cookies</p>
							<p className="text-xs text-gray-300">
								Utilizamos cookies esenciales para el funcionamiento del sitio y cookies opcionales para
								análisis y marketing. Puedes gestionar tus preferencias en cualquier momento.
							</p>
						</div>
					</div>

					<div className="flex items-center gap-2 flex-shrink-0">
						<motion.button
							whileHover={{ scale: 1.05 }}
							whileTap={{ scale: 0.95 }}
							onClick={handleAcceptAll}
							className="bg-red-600 text-white px-4 py-2 rounded text-sm font-medium hover:bg-red-700 transition-colors font-amarante"
						>
							Aceptar Todo
						</motion.button>
						<motion.button
							whileHover={{ scale: 1.05 }}
							whileTap={{ scale: 0.95 }}
							onClick={handleRejectAll}
							className="bg-gray-800 text-white px-3 py-2 rounded text-sm hover:bg-gray-700 transition-colors font-amarante"
						>
							Solo Esenciales
						</motion.button>
						<motion.button
							whileHover={{ textShadow: "0 0 8px rgba(255,0,0,0.6)" }}
							onClick={openSettingsPanel}
							className="text-gray-300 hover:text-red-400 text-sm underline px-2 font-amarante"
						>
							Configurar
						</motion.button>
					</div>
				</div>
			</div>
		</motion.div>
	);

	// Full settings panel
	const renderSettingsPanel = () => (
		<div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-75">
			<motion.div
				initial={{ opacity: 0, scale: 0.9 }}
				animate={{ opacity: 1, scale: 1 }}
				transition={{ duration: 0.3 }}
				className="bg-gray-900 text-white rounded-lg shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto border-2 border-red-800"
			>
				<div className="sticky top-0 bg-gray-900 border-b border-red-800 p-6 rounded-t-lg">
					<div className="flex items-center justify-between">
						<div className="flex items-center gap-3">
							<FontAwesomeIcon icon={faCog} className="w-6 h-6 text-red-500" />
							<h2 className="text-2xl font-bold font-amarante">Configuración de Cookies</h2>
						</div>
						<motion.button
							whileHover={{ scale: 1.1, color: "#ef4444" }}
							onClick={() => setShowPanel(false)}
							className="text-gray-400 hover:text-white p-2"
							aria-label="Cerrar"
						>
							<FontAwesomeIcon icon={faTimes} className="w-6 h-6" />
						</motion.button>
					</div>
					<p className="text-gray-300 mt-3 font-amarante">
						Gestiona tus preferencias de cookies. Puedes activar o desactivar diferentes categorías según tus
						preferencias.
					</p>
				</div>

				<div className="p-6 space-y-6">
					{Object.entries(cookieCategories).map(([key, category]) => (
						<motion.div
							key={key}
							className="border border-red-800 rounded-lg p-5 bg-gray-800"
							initial={{ opacity: 0, y: 20 }}
							animate={{ opacity: 1, y: 0 }}
							transition={{ delay: 0.1 }}
						>
							<div className="flex items-start justify-between mb-4">
								<div className="flex items-center gap-3">
									<FontAwesomeIcon icon={category.icon} className="w-6 h-6 text-red-500" />
									<div>
										<h3 className="font-semibold text-lg font-amarante flex items-center gap-2">
											{category.title}
											{category.required && (
												<span className="bg-red-900 text-red-200 text-xs px-2 py-1 rounded-full font-medium">
													Obligatorias
												</span>
											)}
										</h3>
										<p className="text-sm text-gray-300 mt-1">{category.description}</p>
									</div>
								</div>
								<label className="flex items-center cursor-pointer">
									<input
										type="checkbox"
										checked={consent[key as keyof ConsentState]}
										onChange={(e) => handleCategoryChange(key as keyof ConsentState, e.target.checked)}
										disabled={category.required}
										className="w-5 h-5 text-red-600 bg-gray-700 border-gray-600 rounded focus:ring-red-500 focus:ring-2 disabled:opacity-50 disabled:cursor-not-allowed"
									/>
								</label>
							</div>

							<div className="mt-4">
								<motion.button
									whileHover={{ backgroundColor: "rgba(239, 68, 68, 0.2)" }}
									onClick={() => toggleSection(key)}
									className="flex items-center gap-2 text-red-400 hover:text-red-300 text-sm font-amarante px-3 py-2 rounded-md"
								>
									<span>{expandedSections[key] ? "Ocultar" : "Ver"} cookies incluidas</span>
									<FontAwesomeIcon
										icon={expandedSections[key] ? faChevronUp : faChevronDown}
										className="w-3 h-3"
									/>
								</motion.button>

								{expandedSections[key] && (
									<motion.div
										initial={{ opacity: 0, height: 0 }}
										animate={{ opacity: 1, height: "auto" }}
										transition={{ duration: 0.3 }}
										className="bg-gray-700 p-4 rounded-lg mt-2"
									>
										<h4 className="text-sm font-medium text-gray-200 mb-3 font-amarante">
											Cookies incluidas:
										</h4>
										<ul className="space-y-2">
											{category.cookies.map((cookie, index) => (
												<li
													key={index}
													className="text-xs text-gray-300 font-mono bg-gray-600 p-2 rounded border border-gray-500"
												>
													{cookie}
												</li>
											))}
										</ul>
									</motion.div>
								)}
							</div>
						</motion.div>
					))}
				</div>

				<div className="sticky bottom-0 bg-gray-900 border-t border-red-800 p-6 rounded-b-lg">
					<div className="flex flex-col sm:flex-row gap-3 justify-end">
						<motion.button
							whileHover={{ scale: 1.05 }}
							whileTap={{ scale: 0.95 }}
							onClick={handleRejectAll}
							className="bg-gray-700 text-white px-6 py-3 rounded-lg hover:bg-gray-600 transition-colors font-amarante"
						>
							Rechazar Opcionales
						</motion.button>
						<motion.button
							whileHover={{ scale: 1.05 }}
							whileTap={{ scale: 0.95 }}
							onClick={handleAcceptAll}
							className="bg-green-700 text-white px-6 py-3 rounded-lg hover:bg-green-600 transition-colors font-amarante"
						>
							Aceptar Todo
						</motion.button>
						<motion.button
							whileHover={{ scale: 1.05 }}
							whileTap={{ scale: 0.95 }}
							onClick={handleSavePreferences}
							className="bg-red-600 text-white px-6 py-3 rounded-lg hover:bg-red-700 transition-colors font-amarante"
						>
							Guardar Preferencias
						</motion.button>
					</div>
					<p className="text-xs text-gray-400 mt-3 text-center font-amarante">
						Tus preferencias se guardarán durante 1 año. Puedes cambiarlas en cualquier momento desde el pie
						de página.
					</p>
				</div>
			</motion.div>
		</div>
	);

	return (
		<>
			{/* Small banner shown on first visit */}
			{showBanner && renderBottomBanner()}

			{/* Settings panel */}
			{showPanel && renderSettingsPanel()}
		</>
	);
};

// Component for footer link to open settings panel
const CookieSettings: React.FC = () => {
	const [showPanel, setShowPanel] = useState(false);
	const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({});
	const [consent, setConsent] = useState<ConsentState>({
		essential: true,
		analytics: false,
		marketing: false,
	});

	// Toggle expanded section
	const toggleSection = (section: string) => {
		setExpandedSections((prev) => ({
			...prev,
			[section]: !prev[section],
		}));
	};

	// Load current consent when opening panel
	useEffect(() => {
		if (showPanel) {
			const savedConsent = localStorage.getItem("cookie-consent");
			if (savedConsent) {
				try {
					const parsedConsent = JSON.parse(savedConsent);
					// Validate the parsed consent object
					if (isValidConsentState(parsedConsent)) {
						setConsent(parsedConsent);
					} else {
						console.warn("Invalid consent state found in localStorage");
						// Fall back to default consent state
						setConsent({
							essential: true,
							analytics: false,
							marketing: false,
						});
					}
				} catch (error) {
					console.error("Failed to parse cookie consent from localStorage:", error);
					// Fall back to default consent state
					setConsent({
						essential: true,
						analytics: false,
						marketing: false,
					});
				}
			}
		}
	}, [showPanel]);

	const cookieCategories: CookieCategories = {
		essential: {
			title: "Cookies Esenciales",
			description: "Necesarias para el funcionamiento básico del sitio web y el procesamiento de pagos.",
			cookies: [
				"__stripe_mid - Identificador de Stripe para procesamiento de pagos",
				"__stripe_sid - Sesión de Stripe para transacciones seguras",
			],
			icon: faShieldAlt,
			required: true,
		},
		analytics: {
			title: "Cookies de Análisis",
			description: "Nos ayudan a entender cómo los visitantes interactúan con nuestro sitio web.",
			cookies: [
				"_ga - Google Analytics: identificador único de usuario",
				"_ga_PP9VQSJ0DK - Google Analytics: datos de sesión específicos",
				"_ga_S55WZQNRYZ - Google Analytics: métricas de comportamiento",
				"_ga_QY3DVNQGQN - Google Analytics: datos de conversión",
			],
			icon: faChartBar,
			required: false,
		},
		marketing: {
			title: "Cookies de Marketing",
			description: "Se utilizan para rastrear visitantes y mostrar contenido relevante.",
			cookies: [
				"tk_ai - Jetpack: identificador de usuario para análisis",
				"tk_or - Jetpack: seguimiento del origen del tráfico",
				"tk_lr - Jetpack: última URL de referencia",
			],
			icon: faBullseye,
			required: false,
		},
	};

	/**
	 * Deletes all cookies that start with a specified prefix.
	 * This function should only be called on the client side (in a browser).
	 *
	 * @param prefix The prefix to match against cookie names.
	 */
	function deleteCookiesByPrefix(prefix: string): void {
		// Check if the code is running in a browser environment
		if (typeof window !== "undefined") {
			// Get the document's cookie string and split it into an array of individual cookies
			const allCookies = document.cookie.split(";");

			// Iterate over each cookie
			for (const cookie of allCookies) {
				// Trim any leading/trailing whitespace
				const trimmedCookie = cookie.trim();
				// Extract the cookie's name (everything before the first '=')
				const cookieParts = trimmedCookie.split("=");
				const cookieName = cookieParts[0];

				// Check if cookieName exists and starts with the given prefix
				if (cookieName?.startsWith(prefix)) {
					// To delete a cookie, we set its expiration date to a date in the past.
					// The path must also be specified to ensure the correct cookie is targeted.
					document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
				}
			}
		}
	}

	/**
	 * An example of how to use the function. You would typically call this from
	 * a client-side component or a useEffect hook.
	 */
	// Example of how to call the function:
	// deleteCookiesByPrefix('_ga');

	const applyConsent = (consentChoices: ConsentState) => {
		// Set consent tracking cookies
		document.cookie = `cookie-consent=true; path=/; max-age=${365 * 24 * 60 * 60}`;
		document.cookie = `cookie-consents=${JSON.stringify(consentChoices)}; path=/; max-age=${365 * 24 * 60 * 60}`;

		// Handle Analytics cookies
		if (!consentChoices.analytics) {
			window["ga-disable-analytics_TRACKING"] = true;
			deleteCookiesByPrefix("_ga");
			deleteCookies(["tk_ai", "tk_or", "tk_lr"]);
		} else {
			window["ga-disable-analytics_TRACKING"] = false;
		}

		// Handle Marketing cookies
		if (!consentChoices.marketing) {
			deleteCookies(["tk_ai", "tk_or", "tk_lr"]);
		}

		localStorage.setItem("cookie-consent", JSON.stringify(consentChoices));
		localStorage.setItem("cookie-consent-timestamp", Date.now().toString());
	};

	const deleteCookies = (cookieNames: string[]) => {
		cookieNames.forEach((name) => {
			document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
		});
	};

	const handleAcceptAll = () => {
		const allConsent: ConsentState = { essential: true, analytics: true, marketing: true };
		setConsent(allConsent);
		applyConsent(allConsent);
		setShowPanel(false);
	};

	const handleRejectAll = () => {
		const minimalConsent: ConsentState = { essential: true, analytics: false, marketing: false };
		setConsent(minimalConsent);
		applyConsent(minimalConsent);
		setShowPanel(false);
	};

	const handleSavePreferences = () => {
		applyConsent(consent);
		setShowPanel(false);
	};

	const handleCategoryChange = (category: keyof ConsentState, value: boolean) => {
		if (category === "essential") return;
		setConsent((prev) => ({ ...prev, [category]: value }));
	};

	return (
		<>
			<motion.button
				whileHover={{ textShadow: "0 0 8px rgba(255,0,0,0.6)" }}
				onClick={() => setShowPanel(true)}
				className="text-gray-400 hover:text-red-400 transition-colors !text-sm"
			>
				Configuración de Cookies
			</motion.button>

			{showPanel && (
				<div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-75">
					<motion.div
						initial={{ opacity: 0, scale: 0.9 }}
						animate={{ opacity: 1, scale: 1 }}
						transition={{ duration: 0.3 }}
						className="bg-gray-900 text-white rounded-lg shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto border-2 border-red-800"
					>
						<div className="sticky top-0 bg-gray-900 border-b border-red-800 p-6 rounded-t-lg">
							<div className="flex items-center justify-between">
								<div className="flex items-center gap-3">
									<FontAwesomeIcon icon={faCog} className="w-6 h-6 text-red-500" />
									<h2 className="text-2xl font-bold font-amarante">Configuración de Cookies</h2>
								</div>
								<motion.button
									whileHover={{ scale: 1.1, color: "#ef4444" }}
									onClick={() => setShowPanel(false)}
									className="text-gray-400 hover:text-white p-2"
								>
									<FontAwesomeIcon icon={faTimes} className="w-6 h-6" />
								</motion.button>
							</div>
						</div>

						<div className="p-6 space-y-6">
							{Object.entries(cookieCategories).map(([key, category]) => (
								<motion.div
									key={key}
									className="border border-red-800 rounded-lg p-5 bg-gray-800"
									initial={{ opacity: 0, y: 20 }}
									animate={{ opacity: 1, y: 0 }}
									transition={{ delay: 0.1 }}
								>
									<div className="flex items-start justify-between mb-4">
										<div className="flex items-center gap-3">
											<FontAwesomeIcon icon={category.icon} className="w-6 h-6 text-red-500" />
											<div>
												<h3 className="font-semibold text-lg font-amarante flex items-center gap-2">
													{category.title}
													{category.required && (
														<span className="bg-red-900 text-red-200 text-xs px-2 py-1 rounded-full">
															Obligatorias
														</span>
													)}
												</h3>
												<p className="text-sm text-gray-300 mt-1">{category.description}</p>
											</div>
										</div>
										<label className="flex items-center cursor-pointer">
											<input
												type="checkbox"
												checked={consent[key as keyof ConsentState]}
												onChange={(e) => handleCategoryChange(key as keyof ConsentState, e.target.checked)}
												disabled={category.required}
												className="w-5 h-5 text-red-600 bg-gray-700 border-gray-600 rounded focus:ring-red-500 focus:ring-2 disabled:opacity-50"
											/>
										</label>
									</div>

									<div className="mt-4">
										<motion.button
											whileHover={{ backgroundColor: "rgba(239, 68, 68, 0.2)" }}
											onClick={() => toggleSection(key)}
											className="flex items-center gap-2 text-red-400 hover:text-red-300 text-sm font-amarante px-3 py-2 rounded-md"
										>
											<span>{expandedSections[key] ? "Ocultar" : "Ver"} cookies incluidas</span>
											<FontAwesomeIcon
												icon={expandedSections[key] ? faChevronUp : faChevronDown}
												className="w-3 h-3"
											/>
										</motion.button>

										{expandedSections[key] && (
											<motion.div
												initial={{ opacity: 0, height: 0 }}
												animate={{ opacity: 1, height: "auto" }}
												transition={{ duration: 0.3 }}
												className="bg-gray-700 p-4 rounded-lg mt-2"
											>
												<h4 className="text-sm font-medium text-gray-200 mb-3 font-amarante">
													Cookies incluidas:
												</h4>
												<ul className="space-y-2">
													{category.cookies.map((cookie, index) => (
														<li
															key={index}
															className="text-xs text-gray-300 font-mono bg-gray-600 p-2 rounded border border-gray-500"
														>
															{cookie}
														</li>
													))}
												</ul>
											</motion.div>
										)}
									</div>
								</motion.div>
							))}
						</div>

						<div className="sticky bottom-0 bg-gray-900 border-t border-red-800 p-6 rounded-b-lg">
							<div className="flex flex-col sm:flex-row gap-3 justify-end">
								<motion.button
									whileHover={{ scale: 1.05 }}
									whileTap={{ scale: 0.95 }}
									onClick={handleRejectAll}
									className="bg-gray-700 text-white px-6 py-3 rounded-lg hover:bg-gray-600 transition-colors font-amarante"
								>
									Rechazar Opcionales
								</motion.button>
								<motion.button
									whileHover={{ scale: 1.05 }}
									whileTap={{ scale: 0.95 }}
									onClick={handleAcceptAll}
									className="bg-green-700 text-white px-6 py-3 rounded-lg hover:bg-green-600 transition-colors font-amarante"
								>
									Aceptar Todo
								</motion.button>
								<motion.button
									whileHover={{ scale: 1.05 }}
									whileTap={{ scale: 0.95 }}
									onClick={handleSavePreferences}
									className="bg-red-600 text-white px-6 py-3 rounded-lg hover:bg-red-700 transition-colors font-amarante"
								>
									Guardar Preferencias
								</motion.button>
							</div>
						</div>
					</motion.div>
				</div>
			)}
		</>
	);
};

export { CookieBanner, CookieSettings };
